import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { errorMessage, gebruiktMockApi, http } from './api'
import {
  mockMaakTaak,
  mockTaken,
  mockVerwijderTaak,
  mockWijzigTaak,
  mockZetTaakUren,
} from './__fixtures__/beheer-fixtures'
import { vertraag } from './__fixtures__/vertraag'
import { medewerkerKeys } from './medewerker-api'
import type { NieuweTaak, Taak, TaakWijziging, TakenOverzicht } from './taak-types'

export interface TaakApi {
  list(semesterId: string): Promise<TakenOverzicht>
  maak(input: NieuweTaak): Promise<Taak>
  wijzig(taakId: string, input: TaakWijziging): Promise<Taak>
  verwijder(taakId: string): Promise<void>
  zetUren(
    semesterId: string,
    taakId: string,
    medewerkerId: string,
    uren: number,
  ): Promise<TakenOverzicht>
}

class RealTaakApi implements TaakApi {
  list(semesterId: string): Promise<TakenOverzicht> {
    return http.get<TakenOverzicht>('/api/taken', { semester: semesterId })
  }
  maak(input: NieuweTaak): Promise<Taak> {
    return http.post<Taak>('/api/taken', input)
  }
  wijzig(taakId: string, input: TaakWijziging): Promise<Taak> {
    return http.patch<Taak>(`/api/taken/${taakId}`, input)
  }
  verwijder(taakId: string): Promise<void> {
    return http.del<void>(`/api/taken/${taakId}`)
  }
  zetUren(
    semesterId: string,
    taakId: string,
    medewerkerId: string,
    uren: number,
  ): Promise<TakenOverzicht> {
    return http.put<TakenOverzicht>(
      `/api/taken/${taakId}/uren`,
      { medewerkerId, uren },
      { semester: semesterId },
    )
  }
}

class MockTaakApi implements TaakApi {
  list(semesterId: string): Promise<TakenOverzicht> {
    return vertraag(mockTaken(semesterId))
  }
  maak(input: NieuweTaak): Promise<Taak> {
    return vertraag(mockMaakTaak(input), 160)
  }
  wijzig(taakId: string, input: TaakWijziging): Promise<Taak> {
    return vertraag(mockWijzigTaak(taakId, input), 120)
  }
  verwijder(taakId: string): Promise<void> {
    mockVerwijderTaak(taakId)
    return vertraag(undefined, 120)
  }
  zetUren(
    semesterId: string,
    taakId: string,
    medewerkerId: string,
    uren: number,
  ): Promise<TakenOverzicht> {
    return vertraag(mockZetTaakUren(semesterId, taakId, medewerkerId, uren), 100)
  }
}

export const taakApi: TaakApi = gebruiktMockApi ? new MockTaakApi() : new RealTaakApi()

export const taakKeys = {
  alle: ['taken'] as const,
  lijst: (semesterId: string) => ['taken', 'lijst', semesterId] as const,
}

export function useTaken(semesterId: string) {
  return useQuery({
    queryKey: taakKeys.lijst(semesterId),
    queryFn: () => taakApi.list(semesterId),
    enabled: semesterId !== '',
  })
}

function useHerrekening(semesterId: string) {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: taakKeys.lijst(semesterId) })
    queryClient.invalidateQueries({ queryKey: medewerkerKeys.team(semesterId) })
  }
}

export function useMaakTaak(semesterId: string) {
  const herreken = useHerrekening(semesterId)
  return useMutation({
    mutationFn: (input: NieuweTaak) => taakApi.maak(input),
    onSuccess: (taak) => {
      herreken()
      toast.success(`${taak.naam} toegevoegd`)
    },
    onError: (error) => toast.error(errorMessage(error, 'Toevoegen mislukt')),
  })
}

export function useWijzigTaak(semesterId: string) {
  const herreken = useHerrekening(semesterId)
  return useMutation({
    mutationFn: (vars: { taakId: string; input: TaakWijziging }) =>
      taakApi.wijzig(vars.taakId, vars.input),
    onSuccess: () => {
      herreken()
      toast.success('Taak bijgewerkt')
    },
    onError: (error) => toast.error(errorMessage(error, 'Bijwerken mislukt')),
  })
}

export function useVerwijderTaak(semesterId: string) {
  const herreken = useHerrekening(semesterId)
  return useMutation({
    mutationFn: (taakId: string) => taakApi.verwijder(taakId),
    onSuccess: () => {
      herreken()
      toast.success('Taak verwijderd')
    },
    onError: (error) => toast.error(errorMessage(error, 'Verwijderen mislukt')),
  })
}

export function useZetTaakUren(semesterId: string) {
  const queryClient = useQueryClient()
  const herreken = useHerrekening(semesterId)

  return useMutation({
    mutationFn: (vars: { taakId: string; medewerkerId: string; uren: number }) =>
      taakApi.zetUren(semesterId, vars.taakId, vars.medewerkerId, vars.uren),
    onSuccess: (overzicht) => {
      queryClient.setQueryData(taakKeys.lijst(semesterId), overzicht)
      queryClient.invalidateQueries({ queryKey: medewerkerKeys.team(semesterId) })
      toast.success('Uren opgeslagen')
    },
    onError: (error) => {
      herreken()
      toast.error(errorMessage(error, 'Opslaan mislukt'))
    },
  })
}
